import { useEffect } from 'react';
import { ApiError, request } from '../api/client';

/**
 * The referral (owner, 2026-11-01; infra shared/referral.ts): a published page's "Made with Saddle · Make
 * yours" opens the builder with `?ref=<that app>`. Kept here, first touch, for 60 days; once signed in it
 * is told to the server once, which records who sent this account. The owner of that page gets a free month
 * when this account first pays.
 */
const KEY = 'saddle.ref';
const SENT = 'saddle.ref.sent';
const SHAPE = /^[A-Za-z0-9_-]{4,80}$/;
const KEEP_MS = 60 * 24 * 3600 * 1000;

export function captureReferral(search: string = typeof location !== 'undefined' ? location.search : ''): void {
  try {
    const ref = new URLSearchParams(search).get('ref');
    if (!ref || !SHAPE.test(ref) || keptReferral()) return;
    localStorage.setItem(KEY, JSON.stringify({ ref, at: Date.now() }));
  } catch {
    /* no referral kept: nothing breaks */
  }
}

export function keptReferral(): string | null {
  try {
    const kept = JSON.parse(localStorage.getItem(KEY) ?? 'null') as { ref?: unknown; at?: unknown } | null;
    if (!kept || typeof kept.ref !== 'string' || !SHAPE.test(kept.ref) || typeof kept.at !== 'number') return null;
    return Date.now() - kept.at <= KEEP_MS ? kept.ref : null;
  } catch {
    return null;
  }
}

/** Told once per account: the server keeps the first and ignores the rest, so a second account here may tell it too. */
export function useReferralClaim(userId: string | undefined): void {
  useEffect(() => {
    if (!userId) return;
    const ref = keptReferral();
    if (!ref) return;
    try {
      if (localStorage.getItem(SENT) === `${userId}:${ref}`) return;
    } catch {
      return;
    }
    const told = (): void => {
      try {
        localStorage.setItem(SENT, `${userId}:${ref}`);
      } catch {
        /* told again next time; the server keeps the first */
      }
    };
    // An answer, even a refusal, is final; only a failure to reach the server is tried again next time.
    void request<{ recorded: boolean }>('/billing/referral', { method: 'POST', body: { ref } }).then(told, (e: unknown) => {
      if (e instanceof ApiError && typeof e.status === 'number' && e.status >= 400 && e.status < 500) told();
    });
  }, [userId]);
}
